import { type CtxLoadTrigger, DEFERRABLE_CTX_LOAD_TRIGGERS } from "./ctxLoadTrigger";

export type ReloadRsid = { id: number; region: string };

const sameRsid = (a: ReloadRsid | undefined | null, b: ReloadRsid | undefined | null) =>
  !!a && !!b && a.id === b.id && a.region === b.region;

type PendingReload = { rsid: ReloadRsid; triggers: Set<CtxLoadTrigger>; done: Promise<void> };

/**
 * Decides when the service ctx is reloaded (TR-5544). One per service store, so one per browser tab.
 *
 * - Coalescing: a reload requested while any load runs does not start a second one in parallel, and
 *   is not dropped either (which used to lose updates such as a backup finishing mid-load). It joins the
 *   single trailing reload that starts once every running load is over. Requests in the same tick join
 *   too. Everything that joined travels along as `merged`, so the load still names all its causes.
 * - Every caller's promise resolves after a load that started after its request, so awaiting a reload
 *   still guarantees fresh data.
 * - Hidden tabs: automatic reloads (DEFERRABLE_CTX_LOAD_TRIGGERS) are held back while the tab is hidden
 *   and run as one `visible_again` load when it is shown, unless another load of that server starts
 *   first and answers them. Customer actions are never held back: they await their reload.
 * - A reload for a server the panel has left, or is leaving, is dropped.
 */
export function createCtxReloadScheduler(options: {
  /** The server whose ctx the panel shows right now: a reload request reloads this one */
  currentRsid: () => ReloadRsid | undefined;
  /**
   * The server the panel is on: the one it shows, or the one it switches to while that load runs, and
   * still after it failed. Undefined while the panel only knows a gameserver ID.
   */
  targetRsid: () => ReloadRsid | undefined;
  isHidden: () => boolean;
  /** Runs one ctx load, normally the store's loadAll (which reports its own errors) */
  load: (rsid: ReloadRsid, trigger: CtxLoadTrigger, merged: CtxLoadTrigger[]) => Promise<void>;
}) {
  // Every load that has not settled yet. Several can overlap (a route load next to a reload that was
  // already running), and a reload has to wait for all of them.
  const running = new Set<Promise<unknown>>();
  let pending: PendingReload | null = null;
  let deferred: { rsid: ReloadRsid; triggers: Set<CtxLoadTrigger> } | null = null;

  /** Registers a load that is about to run, whatever started it, so no reload overlaps it */
  function track<T>(load: Promise<T>): Promise<T> {
    running.add(load);
    const forget = () => running.delete(load);
    load.then(forget, forget);
    return load;
  }

  async function waitForRunningLoads() {
    while (running.size) await Promise.allSettled([...running]);
  }

  async function runWhenIdle(rsid: ReloadRsid, triggers: Set<CtxLoadTrigger>) {
    // Let requests from the same tick join before anything starts
    await Promise.resolve();
    await waitForRunningLoads();
    // From here on a new request starts the next trailing reload: this one may already be too old for it
    if (pending?.triggers === triggers) pending = null;
    // The panel moved on to another server, whose own load is fresh anyway
    if (!sameRsid(options.targetRsid(), rsid)) return;
    const [trigger, ...merged] = triggers;
    await options.load(rsid, trigger, merged);
  }

  function schedule(rsid: ReloadRsid, requested: CtxLoadTrigger[]): Promise<void> {
    if (pending && sameRsid(pending.rsid, rsid)) {
      for (const trigger of requested) pending.triggers.add(trigger);
      return pending.done;
    }
    const triggers = new Set(requested);
    pending = { rsid, triggers, done: runWhenIdle(rsid, triggers) };
    return pending.done;
  }

  /** Reloads the ctx of the current server */
  function request(trigger: CtxLoadTrigger): Promise<void> {
    const rsid = options.currentRsid();
    if (!rsid) return Promise.resolve();
    if (DEFERRABLE_CTX_LOAD_TRIGGERS.has(trigger) && options.isHidden()) {
      // A reload of this server that has not started yet answers it as well
      if (pending && sameRsid(pending.rsid, rsid)) return schedule(rsid, [trigger]);
      if (!deferred || !sameRsid(deferred.rsid, rsid)) deferred = { rsid, triggers: new Set() };
      deferred.triggers.add(trigger);
      return Promise.resolve();
    }
    return schedule(rsid, [trigger]);
  }

  /**
   * A ctx load of `rsid` is about to send its request, whatever started it. It answers the reloads held
   * back for that server too, so they are no longer held; returns them for the load's `merged`.
   */
  function started(rsid: ReloadRsid): CtxLoadTrigger[] {
    if (!deferred || !sameRsid(deferred.rsid, rsid)) return [];
    const held = [...deferred.triggers];
    deferred = null;
    return held;
  }

  /** The tab became visible: run the reloads held back while it was hidden, as one load */
  function shown(): Promise<void> {
    const held = deferred;
    deferred = null;
    if (!held || !sameRsid(options.targetRsid(), held.rsid)) return Promise.resolve();
    return schedule(held.rsid, ["visible_again", ...held.triggers]);
  }

  return { track, request, started, shown };
}
